import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useState, useEffect, useRef, useMemo } from "react";
import { fetcher } from "../../utils/fetcher";
import { useTranslation } from "../../i18n";

export interface MunicipalitySuggestion {
  name: string;
  latitude: number;
  longitude: number;
}

interface InputMunicipalityProps {
  editMode?: boolean;
  selectedTowns: MunicipalitySuggestion[];
  onTownsChange: (towns: MunicipalitySuggestion[]) => void;
  label?: string;
}

export const InputMunicipality = ({
  editMode = true,
  selectedTowns,
  onTownsChange,
  label,
}: InputMunicipalityProps) => {
  const { t } = useTranslation();
  const fieldLabel = label ?? t("municipality.label");
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<MunicipalitySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const normalizedSelectedTowns = useMemo(
    () => selectedTowns || [],
    [selectedTowns],
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!editMode || !inputValue.trim()) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await fetcher<MunicipalitySuggestion[]>({
          url: `/address/suggest-municipality?query=${encodeURIComponent(inputValue)}`,
        });
        setOptions(
          (data ?? []).filter(
            (item) =>
              !normalizedSelectedTowns.some((t) => t.name === item.name),
          ),
        );
      } catch {
        setOptions([]);
      }
      setLoading(false);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [editMode, inputValue, normalizedSelectedTowns]);

  return (
    <Autocomplete
      disablePortal
      multiple
      size="small"
      fullWidth
      options={options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      value={normalizedSelectedTowns}
      onChange={(_, newValue) => {
        const unique = Array.from(
          new Map(
            (newValue as MunicipalitySuggestion[]).map((item) => [
              item.name,
              item,
            ]),
          ).values(),
        );
        onTownsChange(unique);
        setInputValue("");
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
        }
      }}
      loading={loading}
      loadingText={t("common.loading")}
      filterOptions={(opts) => opts}
      disabled={!editMode}
      noOptionsText={
        inputValue.length < 1
          ? t("municipality.typeToStart")
          : t("municipality.noResults")
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={fieldLabel}
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};
